import { useEffect, useState } from 'react';
import { X, Plus, Trash2, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import api from '../api';
import { toast } from 'sonner';

const colors = [
  { value: 'coral', cls: 'bg-[#E5614C]' },
  { value: 'teal', cls: 'bg-[#5DCAB5]' },
  { value: 'yellow', cls: 'bg-[#FCD968]' },
];

export default function CategoryManager({ categories, onClose, onChange }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('coral');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post('/event-categories', { name: name.trim(), color });
      setName(''); setColor('coral');
      toast.success('Category added');
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Delete category “${label}”? Events using it will become uncategorized.`)) return;
    try {
      await api.delete(`/event-categories/${id}`);
      toast.success('Category deleted');
      onChange?.();
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center px-5" onClick={onClose}>
      <div className="bg-[#16131a] border border-white/10 rounded-[20px] max-w-md w-full p-7 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-serif-display text-[22px] font-bold text-white">Event Categories</h2>
            <p className="text-[12px] text-white/55 mt-0.5">Organize events by type.</p>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/70">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleAdd} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-white/75 text-[12px] font-semibold uppercase tracking-[0.14em]">New Category Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Concerts"
              className="h-11 rounded-lg bg-[#0f0d12] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/75 text-[12px] font-semibold uppercase tracking-[0.14em]">Color Tag</Label>
            <div className="flex gap-2">
              {colors.map((c) => (
                <button key={c.value} type="button" onClick={() => setColor(c.value)}
                  className={`h-9 w-9 rounded-full ${c.cls} ${color === c.value ? 'ring-2 ring-white ring-offset-2 ring-offset-[#16131a]' : 'opacity-60 hover:opacity-100'} transition-opacity`}>
                </button>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={busy || !name.trim()}
            className="w-full h-10 rounded-lg bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Add Category</>}
          </Button>
        </form>

        <div className="mt-6 space-y-1.5 max-h-[280px] overflow-y-auto">
          {categories.length === 0 && <div className="text-white/45 text-[13px] text-center py-4">No categories yet.</div>}
          {categories.map((c) => {
            const cl = colors.find((cc) => cc.value === c.color) || colors[0];
            return (
              <div key={c.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.04] group">
                <div className={`h-3 w-3 rounded-full ${cl.cls}`} />
                <div className="flex-1 text-[14px] font-medium text-white">{c.name}</div>
                <button onClick={() => handleDelete(c.id, c.name)} className="opacity-0 group-hover:opacity-100 h-7 w-7 rounded-md hover:bg-red-500/15 flex items-center justify-center text-white/55 hover:text-red-400 transition-opacity">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
